import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'motion/react';
import { usePageContext } from '../../contexts/PageContext';
import logo from '../../assets/logos/Proposio-Logo-White-Short.png';
import noTextLogo from '../../assets/logos/Proposio-Logo-White-Icon.png';
import { ROUTES } from 'config/route-configuration';
import { MAIN_NAVIGATION, NavDestination } from 'config/navigation/navigation';
import NavigationItem from 'components/navigation/NavigationItem';
import { can } from 'lib/permissions/can';
import { useUser } from 'contexts/UserContext';
import { dashboardTransitions } from 'lib/motion/dashboardMotion';

const filterNavigationByPermission = (
  items: NavDestination[],
  currentUser: { role?: string | null } | null
): NavDestination[] => {
  return items
    .filter((item) => {
      if (!item.requiredPermission) return true;
      return can(currentUser, item.requiredPermission);
    })
    .map((item) => {
      if (!item.children) return item;

      return {
        ...item,
        children: filterNavigationByPermission(item.children, currentUser),
      };
    })
    .filter((item) => {
      if (item.children && item.children.length === 0) return false;
      return true;
    });
};

const GradientChevron = ({ direction }: { direction: 'left' | 'right' }) => {
  const gradientId = direction === 'left' ? 'sidebar-chevron-gradient-left' : 'sidebar-chevron-gradient-right';
  const points = direction === 'left' ? '14.5 5.5 9.5 10.5 14.5 15.5' : '9.5 5.5 14.5 10.5 9.5 15.5';

  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(79,70,229,0.95)" />
          <stop offset="100%" stopColor="rgba(147,51,234,0.95)" />
        </linearGradient>
      </defs>
      <polyline
        points={points}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Sidebar: React.FC = () => {
  const { pageConfig, isSidebarOpen, setSidebarOpen, isSidebarCollapsed, toggleSidebarCollapsed, isSidebarLocked } =
    usePageContext();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { currentUser, loading: userLoading } = useUser();

  if (!pageConfig.showSidebar) {
    return null;
  }

  const isItemActive = (item: NavDestination, pathname: string): boolean => {
    if (item.path === pathname) return true;

    if (item.children) {
      return item.children.some((child) => child.path === pathname);
    }

    return false;
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const sidebarItems = userLoading ? [] : filterNavigationByPermission(MAIN_NAVIGATION, currentUser);

  const desktopWidth = isSidebarCollapsed ? 88 : 272;

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            key="sidebar-overlay"
            className="fixed inset-0 z-40 bg-black/35 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.fadeFast}
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        layout
        transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.layoutStrong}
        className={`
          fixed left-0 top-0 z-50 h-full border-r border-white/20 bg-gradient-to-b from-indigo-600/90 to-purple-600/90 text-white
          lg:static lg:z-auto
        `}
        animate={{
          x: isSidebarOpen ? 0 : -320,
          width: desktopWidth,
        }}
        initial={false}
        style={{ boxShadow: '0 12px 32px rgba(15,23,42,0.08)' }}
      >
        <motion.div layout className="relative flex h-full flex-col">
          {/* Logo and Toggle Button */}
          <motion.div layout className="flex items-center justify-center px-4 py-4">
            <Link to={ROUTES.DASHBOARD} className="flex w-full items-center justify-center">
              <AnimatePresence mode="wait" initial={false}>
                {isSidebarCollapsed ? (
                  <motion.img
                    key="logo-mark"
                    src={noTextLogo}
                    alt="Proposio Logo"
                    className="h-12 w-auto object-contain"
                    initial={{ opacity: 0, scale: 0.96, y: 2 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98, y: -2 }}
                    transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
                  />
                ) : (
                  <motion.img
                    key="logo-full"
                    src={logo}
                    alt="Proposio Logo"
                    className="h-12 w-auto object-contain"
                    initial={{ opacity: 0, scale: 0.98, y: 2 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98, y: -2 }}
                    transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
                  />
                )}
              </AnimatePresence>
            </Link>
          </motion.div>

          {/* Toggle Button - Vertically centered on sidebar edge */}
          {!isSidebarLocked && (
            <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 z-10">
              <motion.button
                onClick={toggleSidebarCollapsed}
                className="hidden h-8 w-8 items-center justify-center rounded-lg border border-white/35 bg-white/35 backdrop-blur-md hover:bg-white/45 lg:flex"
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                whileHover={prefersReducedMotion ? undefined : { scale: 1.04, x: 1 }}
                whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
                transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
              >
                <GradientChevron direction={isSidebarCollapsed ? 'right' : 'left'} />
              </motion.button>
            </div>
          )}

          {/* Navigation */}
          <motion.nav layout className="flex-1 space-y-2 overflow-y-auto px-4 py-6">
            <LayoutGroup id="sidebar-nav">
              {sidebarItems.map((item) => {
                const hasChildren = Boolean(item.children?.length);
                const isActive = isItemActive(item, location.pathname);
                const isExpanded = expandedItems.has(item.id);

                return (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    isActive={isActive}
                    isExpanded={isExpanded}
                    hasChildren={hasChildren}
                    onToggle={() => toggleExpanded(item.id)}
                    isCollapsed={isSidebarCollapsed}
                  >
                    {item.children?.map((child) => (
                      <NavigationItem
                        key={child.id}
                        item={child}
                        isActive={child.path === location.pathname}
                        isExpanded={false}
                        hasChildren={false}
                        isCollapsed={isSidebarCollapsed}
                        level={1}
                      />
                    ))}
                  </NavigationItem>
                );
              })}
            </LayoutGroup>
          </motion.nav>
        </motion.div>
      </motion.aside>
    </>
  );
};

export default Sidebar;
